import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { Star, ShoppingBag, ArrowLeft, ShieldCheck, Truck, ChevronRight, ShoppingCart, Zap, RotateCcw, Award, Lock } from 'lucide-react';
import { useCart } from '../../context/CartContext';

export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  
  // States for Amazon-like sections
  const [sameCategoryProducts, setSameCategoryProducts] = useState([]);
  const [relevantProducts, setRelevantProducts] = useState([]);
  const [relatedBoughtProducts, setRelatedBoughtProducts] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);

  // Dynamic variants / options
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('2. Animals');
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const [isZoomed, setIsZoomed] = useState(false);
  const [bgPosition, setBgPosition] = useState('0% 0%');

  // Fetch product details and dynamic Amazon-style sections
  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setLoading(true);
        const docRef = doc(db, 'products', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const currentProduct = { id: docSnap.id, ...data };
          setProduct(currentProduct);

          if (data.sizes && data.sizes.length > 0) setSelectedSize(data.sizes[0]);
          if (data.colors && data.colors.length > 0) setSelectedColor(data.colors[0]);
          if (data.styles && data.styles.length > 0) setSelectedStyle(data.styles[0]);

          // 1. Load more products from the same category
          if (data.category) {
            const catQuery = query(
              collection(db, 'products'),
              where('category', '==', data.category),
              limit(5)
            );
            const catSnap = await getDocs(catQuery);
            setSameCategoryProducts(
              catSnap.docs.filter(d => d.id !== id).map(d => ({ id: d.id, ...d.data() }))
            );
          }

          // 2. Relevant items customers are likely to buy
          const relQuery = query(collection(db, 'products'), limit(5));
          const relSnap = await getDocs(relQuery);
          setRelevantProducts(
            relSnap.docs.filter(d => d.id !== id).map(d => ({ id: d.id, ...d.data() }))
          );

          // 3. Related items bought by customers
          const boughtQuery = query(collection(db, 'products'), limit(6));
          const boughtSnap = await getDocs(boughtQuery);
          setRelatedBoughtProducts(
            boughtSnap.docs.filter(d => d.id !== id).map(d => ({ id: d.id, ...d.data() }))
          );
        }
      } catch (error) {
        console.error("Error fetching product details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) return <div className="text-center py-24 text-gray-400 font-medium">Loading product details...</div>;
  if (!product) return <div className="text-center py-24 text-gray-500 font-medium">Product not found!</div>;

  const availableSizes = product.sizes || [];
  const availableColors = product.colors || [];
  const availableStyles = product.styles || ['2. Animals', '1. Multi', '3. Unicorns', '4. Disney'];
  
  // Multiple images support (fallback to single image array if available)
  const productImages = product.images && product.images.length > 0 
    ? product.images 
    : [product.image || "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=500"];

  const currentImage = productImages[activeImageIndex] || productImages[0];

  const bulletFeatures = product.features || [
    "Developing scissor skills with fun, crafty activities for preschoolers.",
    "Child-safe scissors included for secure hands-on practice.",
    "High quality materials ensuring durability and hours of entertainment."
  ];

  const handleMouseMove = (e) => {
    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setBgPosition(`${x}% ${y}%`);
  };

  const handleAddToCart = () => {
    addToCart({ 
      ...product, 
      quantity: qty, 
      selectedSize, 
      selectedColor,
      selectedStyle 
    });
    alert(`Successfully added to cart!`);
  };

  // Reusable Product Card Component for Sections
  const renderProductCard = (item) => (
    <div key={item.id} className="min-w-[200px] sm:min-w-[220px] bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-200 flex flex-col justify-between overflow-hidden group">
      <div>
        <Link to={`/product/${item.id}`} className="block relative overflow-hidden bg-gray-100 h-44">
          <img 
            src={item.image || "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=500"} 
            alt={item.name} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
          />
        </Link>
        <div className="p-3">
          <Link to={`/product/${item.id}`}>
            <h3 className="font-bold text-gray-900 text-xs sm:text-sm line-clamp-1 hover:text-indigo-600 transition-colors">
              {item.name}
            </h3>
          </Link>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-sm font-black text-gray-900">₹{item.price}</span>
            <span className="text-[9px] font-bold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100">
              Free Delivery
            </span>
          </div>
        </div>
      </div>
      <div className="p-3 pt-0">
        <button 
          onClick={() => addToCart(item)}
          className="w-full bg-gray-900 hover:bg-black text-white py-2 rounded-xl font-bold text-xs transition flex items-center justify-center space-x-1.5 shadow"
        >
          <ShoppingCart className="h-3.5 w-3.5" />
          <span>Add to Cart</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Breadcrumb Navigation */}
        <div className="flex items-center space-x-2 text-xs text-gray-500 mb-4 font-medium">
          <span className="cursor-pointer hover:text-indigo-600" onClick={() => navigate('/')}>Toys & Games</span>
          <ChevronRight className="h-3 w-3 text-gray-400" />
          <span className="cursor-pointer hover:text-indigo-600">Arts & Crafts</span>
          <ChevronRight className="h-3 w-3 text-gray-400" />
          <span className="text-gray-900 font-bold">Paper Craft</span>
        </div>

        <button onClick={() => navigate(-1)} className="flex items-center text-xs font-bold text-gray-500 hover:text-indigo-600 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Results
        </button>

        {/* Main Product Layout (3 Columns on Desktop: Thumbnails, Main Image + Info, Right Buy Box) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start relative">
          
          {/* Column 1 & 2: Thumbnails & Main Zoom Image */}
          <div className="lg:col-span-7 flex flex-col md:flex-row gap-4 sticky top-6">
            {/* Vertical Thumbnails */}
            <div className="flex md:flex-col space-x-2 md:space-x-0 md:space-y-3 overflow-x-auto">
              {productImages.map((img, i) => (
                <div 
                  key={i} 
                  onClick={() => setActiveImageIndex(i)}
                  className={`w-14 h-14 md:w-16 md:h-16 border rounded-lg overflow-hidden cursor-pointer p-1 bg-gray-50 transition-all ${activeImageIndex === i ? 'border-indigo-600 ring-2 ring-indigo-600' : 'border-gray-200 hover:border-gray-400'}`}
                >
                  <img src={img} alt="thumb" className="w-full h-full object-contain" />
                </div>
              ))}
            </div>

            {/* Main Interactive Image */}
            <div className="flex-1 relative">
              <div 
                className="bg-gray-50 rounded-2xl overflow-hidden flex items-center justify-center p-4 border border-gray-100 relative cursor-crosshair h-[450px]"
                onMouseEnter={() => setIsZoomed(true)}
                onMouseLeave={() => setIsZoomed(false)}
                onMouseMove={handleMouseMove}
              >
                <img 
                  src={currentImage} 
                  alt={product.name} 
                  className="max-h-full max-w-full object-contain select-none pointer-events-none" 
                />
              </div>

              {isZoomed && (
                <div 
                  className="hidden lg:block absolute top-0 left-[calc(100%+1.5rem)] w-[500px] h-[450px] bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 overflow-hidden bg-no-repeat"
                  style={{
                    backgroundImage: `url(${currentImage})`,
                    backgroundPosition: bgPosition,
                    backgroundSize: '230%'
                  }}
                />
              )}
            </div>
          </div>

          {/* Column 3: Middle Details Section */}
          <div className="lg:col-span-5 flex flex-col">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug">
              {product.name}
            </h1>
            
            <div className="flex items-center space-x-2 mt-2">
              <div className="flex items-center text-amber-500">
                <span className="text-sm font-bold text-gray-900 mr-1">{product.rating || '4.4'}</span>
                <Star className="h-4 w-4 fill-current" />
                <Star className="h-4 w-4 fill-current" />
                <Star className="h-4 w-4 fill-current" />
                <Star className="h-4 w-4 fill-current" />
                <Star className="h-4 w-4 text-gray-300" />
              </div>
              <span className="text-gray-300">|</span>
              <span className="text-xs text-indigo-600 font-medium hover:underline cursor-pointer">{product.reviewsCount || '4,113'} ratings</span>
              <span className="text-gray-300">|</span>
              <span className="text-xs text-gray-600">300+ bought in past month</span>
            </div>

            <div className="mt-2 inline-block">
              <span className="bg-gray-900 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                Amazon's Choice
              </span>
            </div>

            <hr className="my-3 border-gray-200" />

            {/* Pricing Section */}
            <div className="mt-1">
              <div className="flex items-baseline space-x-3">
                <span className="text-xs text-red-600 font-bold">-{product.discount || '11%'}</span>
                <span className="text-3xl font-extrabold text-gray-900">₹{product.price}</span>
                {product.originalPrice && (
                  <span className="text-sm text-gray-400 line-through">
                    M.R.P.: ₹{product.originalPrice}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Inclusive of all taxes</p>
            </div>

            {/* Offers Boxes */}
            <div className="mt-4 grid grid-cols-3 gap-2">
              <div className="border border-gray-200 rounded-lg p-2.5 bg-white text-xs">
                <p className="font-bold text-gray-900">Cashback</p>
                <p className="text-[10px] text-gray-600 line-clamp-2 mt-0.5">Upto ₹15.00 cashback...</p>
                <span className="text-[10px] text-indigo-600 font-medium cursor-pointer">4 offers &gt;</span>
              </div>
              <div className="border border-gray-200 rounded-lg p-2.5 bg-white text-xs">
                <p className="font-bold text-gray-900">Bank Offer</p>
                <p className="text-[10px] text-gray-600 line-clamp-2 mt-0.5">Upto ₹1,000.00 discount...</p>
                <span className="text-[10px] text-indigo-600 font-medium cursor-pointer">39 offers &gt;</span>
              </div>
              <div className="border border-gray-200 rounded-lg p-2.5 bg-white text-xs">
                <p className="font-bold text-gray-900">Partner Offers</p>
                <p className="text-[10px] text-gray-600 line-clamp-2 mt-0.5">Get GST invoice & save...</p>
                <span className="text-[10px] text-indigo-600 font-medium cursor-pointer">1 offer &gt;</span>
              </div>
            </div>

            {/* Icon Badges Row */}
            <div className="mt-4 py-3 border-y border-gray-200 flex justify-between items-center text-center">
              <div className="flex flex-col items-center flex-1">
                <Truck className="h-5 w-5 text-indigo-600 mb-1" />
                <span className="text-[10px] text-gray-700 font-medium">Free Delivery</span>
              </div>
              <div className="flex flex-col items-center flex-1">
                <RotateCcw className="h-5 w-5 text-indigo-600 mb-1" />
                <span className="text-[10px] text-gray-700 font-medium">10 days Replacement</span>
              </div>
              <div className="flex flex-col items-center flex-1">
                <Award className="h-5 w-5 text-indigo-600 mb-1" />
                <span className="text-[10px] text-gray-700 font-medium">Top Brand</span>
              </div>
              <div className="flex flex-col items-center flex-1">
                <Lock className="h-5 w-5 text-indigo-600 mb-1" />
                <span className="text-[10px] text-gray-700 font-medium">Secure transaction</span>
              </div>
            </div>

            {/* Style Name Selection */}
            <div className="mt-4">
              <label className="block text-xs font-bold text-gray-800 mb-2">
                Style Name: <span className="font-normal text-indigo-600">{selectedStyle}</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {availableStyles.map((style) => (
                  <button
                    key={style}
                    onClick={() => setSelectedStyle(style)}
                    className={`px-3 py-1.5 border rounded-lg text-xs font-bold transition-all ${
                      selectedStyle === style 
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-600' 
                        : 'border-gray-300 hover:border-gray-400 text-gray-700 bg-white'
                    }`}
                  >
                    {style}
                  </button>
                ))}
              </div>
            </div>

            {/* Conditional Size/Color options if available */}
            {availableSizes.length > 0 && (
              <div className="mt-4">
                <label className="block text-xs font-bold text-gray-800 mb-2">Size</label>
                <div className="flex gap-2">
                  {availableSizes.map(size => (
                    <button key={size} onClick={() => setSelectedSize(size)} className={`px-3 py-1 border rounded text-xs ${selectedSize === size ? 'border-indigo-600 bg-indigo-50 font-bold' : 'border-gray-300'}`}>
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <hr className="my-4 border-gray-200" />

            {/* About this item */}
            <div className="space-y-2">
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">About this item</h3>
              <ul className="list-disc pl-4 space-y-1.5 text-xs text-gray-600 leading-relaxed">
                {bulletFeatures.map((feat, index) => (
                  <li key={index}>{feat}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Column 4: Right Amazon Buy Box (Sticky Card) */}
          <div className="lg:col-span-12 xl:col-span-12 mt-4 lg:mt-0">
            {/* We can place the Buy Box inside a dedicated sidebar card layout or float it */}
          </div>

        </div>

        {/* Dedicated Buy Box Container similar to Amazon Right Panel */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {/* Extra product description or seller info can go here */}
          </div>
          
          <div className="bg-white border border-gray-300 rounded-2xl p-5 shadow-md h-fit">
            <div className="flex items-center space-x-1 text-indigo-600 font-bold text-sm mb-2">
              <Zap className="h-4 w-4 fill-current" />
              <span>Prime Delivery & Checkout</span>
            </div>
            
            <div className="text-2xl font-black text-gray-900 mb-1">₹{product.price}</div>
            <p className="text-xs text-emerald-700 font-semibold mb-3">FREE delivery <span className="text-gray-800 font-bold">Sunday, 2 August</span>.</p>
            
            <p className="text-xs text-gray-600 mb-4">Or fastest delivery <span className="font-bold text-gray-900">Tomorrow</span>. Order within 1 hr 44 mins.</p>

            <div className="text-xs font-bold text-emerald-600 mb-4">In stock</div>

            {/* Quantity Selector */}
            <div className="flex items-center justify-between mb-4 bg-gray-50 p-2 rounded-xl border border-gray-200">
              <span className="text-xs font-bold text-gray-700">Quantity:</span>
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-1 text-gray-600 hover:bg-gray-100 font-bold text-xs">-</button>
                <span className="px-4 py-1 text-xs font-bold text-gray-900">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="px-3 py-1 text-gray-600 hover:bg-gray-100 font-bold text-xs">+</button>
              </div>
            </div>

            <div className="space-y-2.5">
              <button 
                onClick={handleAddToCart}
                className="w-full bg-amber-400 hover:bg-amber-500 text-gray-900 py-3 rounded-xl font-bold text-xs shadow transition flex items-center justify-center space-x-2"
              >
                <ShoppingBag className="h-4 w-4" />
                <span>Add to Cart</span>
              </button>

              <button 
                onClick={() => { handleAddToCart(); navigate('/cart'); }}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-bold text-xs shadow transition flex items-center justify-center space-x-2"
              >
                <span>Buy Now</span>
              </button>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 text-xs space-y-1.5 text-gray-600">
              <div className="flex justify-between">
                <span className="text-gray-500">Ships from</span>
                <span className="font-medium text-gray-900">Amazon</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Sold by</span>
                <span className="font-medium text-indigo-600">Grasper Global</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Payment</span>
                <span className="font-medium text-gray-900">Secure transaction</span>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 1: Load more products same category */}
        {sameCategoryProducts.length > 0 && (
          <div className="mt-20 border-t border-gray-200 pt-10">
            <h2 className="text-lg sm:text-xl font-extrabold text-gray-900 mb-6">
              Load more products from the same category
            </h2>
            <div className="flex overflow-x-auto pb-4 space-x-4 scrollbar-thin scrollbar-thumb-gray-300">
              {sameCategoryProducts.map(renderProductCard)}
            </div>
          </div>
        )}

        {/* SECTION 2: Relevant items customers are likely to buy */}
        {relevantProducts.length > 0 && (
          <div className="mt-12 border-t border-gray-200 pt-10">
            <h2 className="text-lg sm:text-xl font-extrabold text-gray-900 mb-6">
              Relevant items customers are likely to buy
            </h2>
            <div className="flex overflow-x-auto pb-4 space-x-4 scrollbar-thin scrollbar-thumb-gray-300">
              {relevantProducts.map(renderProductCard)}
            </div>
          </div>
        )}

        {/* SECTION 3: Related items bought by customers */}
        {relatedBoughtProducts.length > 0 && (
          <div className="mt-12 border-t border-gray-200 pt-10 mb-16">
            <h2 className="text-lg sm:text-xl font-extrabold text-gray-900 mb-6">
              Related items bought by customers
            </h2>
            <div className="flex overflow-x-auto pb-4 space-x-4 scrollbar-thin scrollbar-thumb-gray-300">
              {relatedBoughtProducts.map(renderProductCard)}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}